/**
 * Create Organization Page
 *
 * Form to create a new CRA or Bank organization
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrganization, getAllOrganizations } from '../../../services/firestore';
import type { Organization, OrgType } from '../../../services/firestore';
import { getRatedInstitutions } from '../../../services/institutionService';
import type { RatedInstitution } from '../../../types/institution';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './CreateOrganization.css';

interface FormData {
  name: string;
  type: OrgType;
  parent_org_id: string;
  analyst_seats: number;
  bank_seats: number;
  institutions_access: string[];
}

const initialFormData: FormData = {
  name: '',
  type: 'cra',
  parent_org_id: '',
  analyst_seats: 10,
  bank_seats: 50,
  institutions_access: [],
};

export const CreateOrganization: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [craOrganizations, setCraOrganizations] = useState<Organization[]>([]);
  const [availableInstitutions, setAvailableInstitutions] = useState<RatedInstitution[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [orgs, institutions] = await Promise.all([
        getAllOrganizations(),
        getRatedInstitutions(),
      ]);
      setCraOrganizations(orgs.filter(o => o.type === 'cra'));
      setAvailableInstitutions(institutions);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('seats') ? parseInt(value) || 0 : value,
    }));

    // Reset parent when switching to CRA
    if (name === 'type' && value === 'cra') {
      setFormData(prev => ({ ...prev, parent_org_id: '' }));
    }
  };

  const toggleInstitution = (institution: string) => {
    setFormData(prev => ({
      ...prev,
      institutions_access: prev.institutions_access.includes(institution)
        ? prev.institutions_access.filter(i => i !== institution)
        : [...prev.institutions_access, institution],
    }));
  };

  const selectAllInstitutions = () => {
    setFormData(prev => ({
      ...prev,
      institutions_access: availableInstitutions.map(i => i.institution_id),
    }));
  };

  const clearAllInstitutions = () => {
    setFormData(prev => ({
      ...prev,
      institutions_access: [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Organization name is required');
      return;
    }

    if (formData.type === 'bank' && !formData.parent_org_id) {
      setError('Parent CRA is required for Bank organizations');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createOrganization({
        name: formData.name.trim(),
        type: formData.type,
        parent_org_id: formData.type === 'bank' ? formData.parent_org_id : null,
        status: 'active',
        licenses: {
          analyst_seats: formData.analyst_seats,
          bank_seats: formData.type === 'cra' ? formData.bank_seats : 0,
          analyst_seats_used: 0,
          bank_seats_used: 0,
        },
        institutions_access: formData.institutions_access,
      });

      navigate('/admin/organizations');
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="create-org-page">
        <h1>Create Organization</h1>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="create-org-page">
      <h1>Create Organization</h1>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="create-org-form">
        {/* Organization Type */}
        <div className="form-group">
          <label>Organization Type</label>
          <div className="radio-group">
            <label className={`radio-option ${formData.type === 'cra' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="type"
                value="cra"
                checked={formData.type === 'cra'}
                onChange={handleInputChange}
              />
              <span className="radio-content">
                <span className="radio-icon">📊</span>
                <span className="radio-label">Credit Rating Agency (CRA)</span>
                <span className="radio-desc">Full access to rate institutions</span>
              </span>
            </label>
            <label className={`radio-option ${formData.type === 'bank' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="type"
                value="bank"
                checked={formData.type === 'bank'}
                onChange={handleInputChange}
              />
              <span className="radio-content">
                <span className="radio-icon">🏦</span>
                <span className="radio-label">Bank (Client of CRA)</span>
                <span className="radio-desc">View-only access to own ratings</span>
              </span>
            </label>
          </div>
        </div>

        {/* Organization Name */}
        <div className="form-group">
          <label htmlFor="name">Organization Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter organization name..."
          />
        </div>

        {/* Parent CRA (for Banks only) */}
        {formData.type === 'bank' && (
          <div className="form-group">
            <label htmlFor="parent_org_id">Parent CRA</label>
            <select
              id="parent_org_id"
              name="parent_org_id"
              value={formData.parent_org_id}
              onChange={handleInputChange}
            >
              <option value="">Select a CRA...</option>
              {craOrganizations.map(cra => (
                <option key={cra.id} value={cra.id}>
                  {cra.name}
                </option>
              ))}
            </select>
            <p className="help-text">
              The CRA that will manage this bank's ratings
            </p>
          </div>
        )}

        {/* Licenses */}
        <div className="form-section">
          <h3>Licenses</h3>
          <div className="licenses-grid">
            <div className="form-group">
              <label htmlFor="analyst_seats">Analyst Seats</label>
              <input
                type="number"
                id="analyst_seats"
                name="analyst_seats"
                value={formData.analyst_seats}
                onChange={handleInputChange}
                min="1"
                max="1000"
              />
              <p className="help-text">
                Maximum number of {formData.type === 'cra' ? 'analysts' : 'users'}
              </p>
            </div>

            {formData.type === 'cra' && (
              <div className="form-group">
                <label htmlFor="bank_seats">Bank Seats</label>
                <input
                  type="number"
                  id="bank_seats"
                  name="bank_seats"
                  value={formData.bank_seats}
                  onChange={handleInputChange}
                  min="0"
                  max="10000"
                />
                <p className="help-text">
                  Maximum client bank users
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Institutions Access */}
        <div className="form-section">
          <div className="section-header">
            <h3>Institutions Access</h3>
            <div className="section-actions">
              <button type="button" onClick={selectAllInstitutions} className="text-btn">
                Select All
              </button>
              <button type="button" onClick={clearAllInstitutions} className="text-btn">
                Clear All
              </button>
            </div>
          </div>

          <div className="institutions-grid">
            {availableInstitutions.map(inst => (
              <label key={inst.institution_id} className="institution-checkbox">
                <input
                  type="checkbox"
                  checked={formData.institutions_access.includes(inst.institution_id)}
                  onChange={() => toggleInstitution(inst.institution_id)}
                />
                <span>{inst.institution_name}</span>
              </label>
            ))}
          </div>
          <p className="help-text">
            {formData.institutions_access.length} of {availableInstitutions.length} institutions selected
          </p>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/organizations')}
            className="cancel-btn"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Organization'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateOrganization;
